import { useEffect, useState } from "react";

import type { TokenCheckInput, TokenCheckResponse } from "@/client/features/auth/auth-api";
import { ApiError } from "@/client/lib/api";

export type TokenStatus = "checking" | "valid" | "invalid";

/**
 * Pre-validate a token-bearing link on load so an expired/unknown link shows a
 * dead-link card before the user fills out the form. A missing token is invalid
 * immediately (no request). Network/rate-limit failures fall through to "valid"
 * so a transient hiccup never dead-ends a genuine link - the real submit still
 * validates server-side; only an explicit `{ valid: false }` marks it invalid.
 */
export function useTokenStatus(
  token: string | null,
  check: (input: TokenCheckInput) => Promise<TokenCheckResponse>,
): TokenStatus {
  const [status, setStatus] = useState<TokenStatus>(token ? "checking" : "invalid");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    let active = true;
    setStatus("checking");
    check({ token })
      .then((res) => {
        if (active) {
          setStatus(res.valid ? "valid" : "invalid");
        }
      })
      .catch((err: unknown) => {
        if (active) {
          // A 400 means the request itself was rejected as malformed; anything
          // else (offline, 429, 5xx) is transient - let the form render.
          setStatus(err instanceof ApiError && err.status === 400 ? "invalid" : "valid");
        }
      });
    return () => {
      active = false;
    };
  }, [token, check]);

  return status;
}
